
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import QuoteForm from '../../../components/feature/QuoteForm';

const StormDamagePage = () => {
  useEffect(() => {
    document.title = 'Storm Damage Roof Repair | Hail Damage Roof | Wind Damage | Emergency Repair NWA';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Emergency storm damage roof repair in Northwest Arkansas. Expert hail damage roof restoration, wind damage roof repair, and storm roof damage repair. 24/7 emergency response for Fayetteville, Springdale, Rogers, Bentonville homes. Free hail damage inspection and hail damage insurance claim assistance available 24/7.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'storm damage roof repair, hail damage roof, wind damage roof, emergency storm damage repair, storm roof damage repair, hail damage insurance claim, wind storm damage repair, emergency roof repair');
    }
  }, []);

  const faqs = [
    {
      question: 'How do I know if my roof has hail damage?',
      answer: 'Hail damage roof signs include dented or bruised shingles, missing granules creating dark spots, cracked or split shingles, dented metal flashing or vents, and damaged gutters or downspouts. However, hail damage is often not visible from the ground. Professional hail damage inspection by certified inspectors is essential to identify all damage for insurance claims. Many homeowners are surprised to learn their roof has significant hail damage that qualifies for full replacement coverage.'
    },
    {
      question: 'Does insurance cover storm damage roof repair?',
      answer: 'Most homeowner insurance policies cover storm damage roof repair including hail damage roof restoration and wind damage roof repair when caused by covered weather events. Your policy typically covers the cost of repairs or replacement minus your deductible. Contractors in our network specialize in hail damage insurance claim assistance, documenting all damage thoroughly and working directly with your insurance adjuster to ensure fair compensation for storm roof damage repair.'
    },
    {
      question: 'How quickly should I repair storm damage to my roof?',
      answer: 'Storm damage roof repair should be addressed as quickly as possible. Even minor damage can allow water infiltration that causes extensive interior damage, mold growth, and structural problems. Emergency storm damage repair services are available 24/7 to provide temporary protection with tarps and emergency repairs, preventing further damage while permanent storm roof damage repair is scheduled. Many contractors also specialize in hail damage insurance claim assistance, ensuring you receive fair compensation for all damage.'
    },
    {
      question: 'What is the difference between hail damage and wind damage?',
      answer: 'Hail damage roof typically shows as bruising, denting, or cracking of shingles from impact, while wind damage roof manifests as lifted, torn, or completely missing shingles. Both types of storm damage roof require professional assessment. Wind storm damage repair often involves replacing sections of shingles, while hail damage may require complete roof replacement if damage is widespread. Professional inspectors document both types for insurance claims.'
    },
    {
      question: 'Can contractors help with my insurance claim for storm damage?',
      answer: 'Absolutely! Contractors in our network specialize in hail damage insurance claim assistance. They provide detailed documentation with photos, meet with your insurance adjuster on-site, provide accurate repair estimates, advocate for fair compensation, and handle all communication with your insurance company. Their experience with storm damage roof repair claims ensures you receive the full coverage you deserve for wind damage roof and hail damage roof restoration.'
    },
    {
      question: 'How long does storm damage roof repair take?',
      answer: 'Emergency storm damage repair with temporary protection can often be completed within 24-48 hours. Complete storm roof damage repair or replacement typically takes 1-3 days depending on roof size and damage extent. Weather conditions may affect scheduling. Contractors prioritize emergency roof repair to prevent further water damage, then schedule permanent repairs as quickly as possible while ensuring quality workmanship.'
    }
  ];

  return (
    <div className="min-h-screen">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-orange-600 to-orange-800">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://public.readdy.ai/ai/img_res/767e36d290fdd12e07b4f9259a75193e.jpg"
            alt="Storm damage roof repair Northwest Arkansas"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <div className="inline-block bg-red-500 text-white px-4 py-2 rounded-full font-semibold text-sm mb-4">
                24/7 EMERGENCY SERVICE
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Emergency Storm Damage Roof Repair in NWA
              </h1>
              <p className="text-xl mb-8 leading-relaxed">
                Connect with expert <strong>storm damage roof repair</strong> contractors throughout Northwest Arkansas. Rapid response to <strong>hail damage roof</strong> restoration, <strong>wind damage roof</strong> repair, and <strong>emergency storm damage repair</strong> for Fayetteville, Springdale, Rogers, Bentonville homes. Free <strong>hail damage inspection</strong> and <strong>hail damage insurance claim</strong> assistance available 24/7.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="tel:4795551234"
                  className="bg-white text-orange-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-orange-50 transition-all shadow-xl text-center whitespace-nowrap"
                >
                  Emergency: (479) 555-1234
                </a>
                <a
                  href="#quote-form"
                  className="bg-gradient-to-r from-slate-700 to-slate-900 text-white px-8 py-4 rounded-lg font-bold text-lg hover:from-slate-800 hover:to-black transition-all shadow-xl text-center whitespace-nowrap"
                >
                  Get Free Assessment
                </a>
              </div>
            </div>
            <div className="hidden lg:block">
              <QuoteForm variant="compact" />
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Expert Storm Damage Roof Repair Services in Northwest Arkansas
              </h2>
              
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                When severe weather strikes Northwest Arkansas, your roof takes the brunt of the damage. <strong>Hail damage roof</strong> problems, <strong>wind damage roof</strong> issues, and <strong>storm damage roof</strong> destruction require immediate professional attention. We connect homeowners throughout Fayetteville, Springdale, Rogers, Bentonville, and surrounding communities with experienced contractors who specialize in <strong>emergency storm damage repair</strong> and complete restoration services. These professionals respond quickly to prevent further damage and work directly with your insurance company to ensure full coverage for <strong>storm roof damage repair</strong>.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Northwest Arkansas experiences some of the most severe weather in the country. Hail storms with golf ball-sized hail, straight-line winds exceeding 70 mph, tornadoes, and severe thunderstorms can devastate roofs in minutes. <strong>Wind damage roof</strong> repair requires specialized expertise to properly assess damage, document for insurance claims, and restore your roof to pre-storm condition. The contractors in our network have decades of combined experience handling all types of <strong>storm damage roof repair</strong> throughout the region.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                Time is critical after storm damage. Even minor damage can allow water infiltration that causes extensive interior damage to ceilings, walls, insulation, and belongings. <strong>Emergency roof repair</strong> services address active leaks quickly, preventing further damage to your home. Contractors provide emergency tarping and temporary repairs within hours of your call, then schedule permanent <strong>storm damage roof repair</strong> as quickly as possible. This rapid response minimizes total damage and associated costs.
              </p>

              <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-8 mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  <i className="ri-alarm-warning-line text-red-600 mr-2"></i>
                  Why Immediate Storm Damage Response Matters
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>Storm damage roof repair</strong> should never be delayed. Even small areas of <strong>hail damage roof</strong> or <strong>wind damage roof</strong> compromise your roof's waterproof barrier. The next rain can cause water to infiltrate your attic, insulation, walls, and ceilings, leading to thousands of dollars in additional damage beyond the original storm damage.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Professional <strong>emergency storm damage repair</strong> contractors provide immediate temporary protection while permanent repairs are scheduled. They install high-quality tarps, secure loose materials, and prevent further water intrusion. This emergency response is often covered by insurance and can save you from catastrophic interior damage.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Additionally, most insurance policies require prompt action to mitigate damage. Delaying <strong>storm roof damage repair</strong> can jeopardize your claim. Contractors document the timeline of damage and repairs, ensuring your insurance company cannot deny coverage due to delayed response.
                </p>
              </div>

              <h3 className="text-3xl font-bold text-gray-900 mb-6">Types of Storm Damage We Address</h3>
              
              <div className="space-y-6 mb-8">
                <div className="bg-orange-50 rounded-lg p-6">
                  <div className="flex items-start">
                    <i className="ri-hail-line text-4xl text-orange-600 mr-4 mt-1"></i>
                    <div>
                      <h4 className="text-2xl font-bold text-gray-900 mb-3">Hail Damage Roof Repair</h4>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        <strong>Hail damage roof</strong> problems are among the most common storm-related issues in Northwest Arkansas. Hail impacts create bruising, denting, cracking, and granule loss on asphalt shingles. Even small hail can cause significant damage that compromises shingle integrity and leads to premature failure.
                      </p>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        Professional <strong>hail damage inspection</strong> identifies all damage, including subtle bruising not visible from the ground. Contractors document damage with detailed photos and measurements suitable for <strong>hail damage insurance claim</strong> submissions. Many homeowners are surprised to learn their seemingly minor hail damage qualifies for complete roof replacement coverage.
                      </p>
                      <p className="text-gray-700 leading-relaxed">
                        <strong>Common hail damage signs:</strong> Dented or bruised shingles, missing granules creating dark spots, cracked or split shingles, dented metal flashing and vents, damaged gutters and downspouts, and broken or cracked skylights.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-orange-50 rounded-lg p-6">
                  <div className="flex items-start">
                    <i className="ri-windy-line text-4xl text-orange-600 mr-4 mt-1"></i>
                    <div>
                      <h4 className="text-2xl font-bold text-gray-900 mb-3">Wind Damage Roof Repair</h4>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        <strong>Wind damage roof</strong> issues occur when high winds lift, tear, or completely remove shingles. Northwest Arkansas experiences frequent severe thunderstorms with straight-line winds exceeding 60-70 mph, capable of causing extensive damage. <strong>Wind storm damage repair</strong> requires immediate attention to prevent water infiltration.
                      </p>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        Contractors assess the extent of <strong>wind damage roof</strong> problems, secure loose materials, and provide emergency protection. They identify not just obvious missing shingles but also lifted shingles that appear intact but have broken seals, compromising their waterproof protection.
                      </p>
                      <p className="text-gray-700 leading-relaxed">
                        <strong>Common wind damage signs:</strong> Missing shingles, lifted or curled shingle edges, torn or ripped shingles, damaged or missing ridge caps, loose or damaged flashing, and debris damage from flying objects.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-orange-50 rounded-lg p-6">
                  <div className="flex items-start">
                    <i className="ri-thunderstorms-line text-4xl text-orange-600 mr-4 mt-1"></i>
                    <div>
                      <h4 className="text-2xl font-bold text-gray-900 mb-3">Combined Storm Damage</h4>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        Many severe weather events cause multiple types of damage simultaneously. A single storm may produce both <strong>hail damage roof</strong> and <strong>wind damage roof</strong> problems, along with water damage from heavy rain. Comprehensive <strong>storm damage roof repair</strong> addresses all issues to fully restore your roof's protection.
                      </p>
                      <p className="text-gray-700 leading-relaxed">
                        Contractors perform thorough inspections to identify all damage types, ensuring nothing is missed in insurance documentation. Their experience with complex <strong>storm roof damage repair</strong> claims ensures you receive full compensation for all damage, not just the most obvious issues.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-orange-50 rounded-lg p-6">
                  <div className="flex items-start">
                    <i className="ri-drop-line text-4xl text-orange-600 mr-4 mt-1"></i>
                    <div>
                      <h4 className="text-2xl font-bold text-gray-900 mb-3">Water Damage & Leaks</h4>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        Storm damage often creates immediate leaks or compromises your roof's waterproof barrier, leading to water damage during subsequent rain. <strong>Emergency roof repair</strong> services address active leaks quickly, preventing extensive interior damage to ceilings, walls, insulation, and belongings.
                      </p>
                      <p className="text-gray-700 leading-relaxed">
                        Contractors provide emergency tarping and temporary repairs within hours of your call, then schedule permanent <strong>storm damage roof repair</strong> as quickly as possible. This rapid response minimizes total damage and associated costs.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <h3 className="text-3xl font-bold text-gray-900 mb-6">Our Storm Damage Repair Process</h3>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <div className="bg-orange-600 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg mr-4 flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Emergency Response & Assessment</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Contractors respond quickly to your call, often within hours for <strong>emergency storm damage repair</strong>. They perform a comprehensive inspection to assess all <strong>hail damage roof</strong>, <strong>wind damage roof</strong>, and related issues. If immediate protection is needed, they install tarps and secure loose materials to prevent further damage.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-orange-600 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg mr-4 flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Detailed Documentation</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Professional <strong>hail damage inspection</strong> includes extensive photo documentation, detailed measurements, and written reports. This documentation is essential for <strong>hail damage insurance claim</strong> submissions and ensures your insurance company has all information needed to approve your claim for <strong>storm roof damage repair</strong>.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-orange-600 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg mr-4 flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Insurance Claim Assistance</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Contractors work directly with your insurance company, meeting with adjusters on-site, providing detailed estimates, and advocating for fair compensation. Their experience with <strong>wind storm damage repair</strong> and <strong>hail damage insurance claim</strong> processes ensures you receive full coverage for all damage.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-orange-600 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg mr-4 flex-shrink-0">
                    4
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Professional Repair or Replacement</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Once your insurance claim is approved, contractors schedule complete <strong>storm damage roof repair</strong> or replacement. They use premium materials, employ certified installation techniques, and complete work efficiently while maintaining the highest quality standards. Most <strong>storm roof damage repair</strong> projects are completed in 1-3 days.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-orange-600 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg mr-4 flex-shrink-0">
                    5
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Final Inspection & Warranty</h4>
                    <p className="text-gray-700 leading-relaxed">
                      After completing <strong>emergency storm damage repair</strong> or replacement, contractors perform a final inspection to ensure all work meets their high standards. They provide comprehensive warranties on both materials and workmanship, giving you peace of mind that your roof is fully protected.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ Section */}
            <div className="bg-blue-50 rounded-2xl p-8 md:p-12 mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
                Storm Damage FAQs
              </h2>
              <div className="space-y-4 max-w-4xl mx-auto">
                <details className="bg-white rounded-lg shadow-md p-6 group">
                  <summary className="font-bold text-lg text-gray-900 cursor-pointer list-none flex items-center justify-between">
                    How do I know if my roof has storm damage?
                    <i className="ri-arrow-down-s-line text-2xl text-orange-600 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <p className="text-gray-700 mt-4 leading-relaxed">
                    Common signs of storm damage include missing or damaged shingles, dents in metal components, granule loss in gutters, water stains on ceilings, and visible damage to roof vents or flashing. However, many types of storm damage aren't visible from the ground. After any severe weather event in Northwest Arkansas, we recommend scheduling a free professional roof inspection to identify all damage, including hidden issues that could lead to leaks.
                  </p>
                </details>

                <details className="bg-white rounded-lg shadow-md p-6 group">
                  <summary className="font-bold text-lg text-gray-900 cursor-pointer list-none flex items-center justify-between">
                    Will my insurance cover storm damage repairs?
                    <i className="ri-arrow-down-s-line text-2xl text-orange-600 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <p className="text-gray-700 mt-4 leading-relaxed">
                    Most homeowners insurance policies cover storm damage, including hail damage and wind damage, minus your deductible. Coverage depends on your specific policy and the extent of damage. Contractors in our network work directly with insurance companies throughout Northwest Arkansas to document all storm damage and help you navigate the claims process. They provide detailed documentation, meet with adjusters, and ensure you receive fair compensation for necessary repairs.
                  </p>
                </details>

                <details className="bg-white rounded-lg shadow-md p-6 group">
                  <summary className="font-bold text-lg text-gray-900 cursor-pointer list-none flex items-center justify-between">
                    How long do I have to file a storm damage claim?
                    <i className="ri-arrow-down-s-line text-2xl text-orange-600 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <p className="text-gray-700 mt-4 leading-relaxed">
                    Most insurance policies require storm damage claims to be filed within one year of the storm event, though some policies may have shorter timeframes. It's crucial to have your roof inspected as soon as possible after severe weather in Fayetteville, Bentonville, Rogers, or Springdale. Early detection and documentation of storm damage ensures you don't miss claim deadlines and prevents minor damage from becoming major problems.
                  </p>
                </details>

                <details className="bg-white rounded-lg shadow-md p-6 group">
                  <summary className="font-bold text-lg text-gray-900 cursor-pointer list-none flex items-center justify-between">
                    What's the difference between hail damage and wind damage?
                    <i className="ri-arrow-down-s-line text-2xl text-orange-600 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <p className="text-gray-700 mt-4 leading-relaxed">
                    Hail damage typically appears as circular dents or bruises on shingles, with granule loss and potential cracking. You may also see dents on metal roof components, vents, and gutters. Wind damage usually involves lifted, torn, or missing shingles, with damage often concentrated on roof edges and ridges. Both types of damage can compromise your roof's integrity and require professional assessment. Certified inspectors identify all types of storm damage during free inspections.
                  </p>
                </details>

                <details className="bg-white rounded-lg shadow-md p-6 group">
                  <summary className="font-bold text-lg text-gray-900 cursor-pointer list-none flex items-center justify-between">
                    Should I repair storm damage myself?
                    <i className="ri-arrow-down-s-line text-2xl text-orange-600 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <p className="text-gray-700 mt-4 leading-relaxed">
                    DIY storm damage repairs are strongly discouraged. Improper repairs can void warranties, fail to address underlying damage, and may not meet insurance requirements. Professional storm damage restoration ensures all damage is properly identified and repaired using correct materials and techniques. Additionally, working on roofs is dangerous without proper safety equipment and training. Certified contractors have the expertise, equipment, and insurance to safely and effectively repair all storm damage.
                  </p>
                </details>

                <details className="bg-white rounded-lg shadow-md p-6 group">
                  <summary className="font-bold text-lg text-gray-900 cursor-pointer list-none flex items-center justify-between">
                    How long does storm damage repair take?
                    <i className="ri-arrow-down-s-line text-2xl text-orange-600 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <p className="text-gray-700 mt-4 leading-relaxed">
                    Storm damage repair timelines vary based on the extent of damage and weather conditions. Minor repairs may take 1-2 days, while complete roof replacements typically take 2-5 days. After major storm events in Northwest Arkansas, there may be increased demand for roofing services. Contractors prioritize emergency repairs and work efficiently to restore your roof as quickly as possible while maintaining high-quality standards. Detailed timelines are provided during the estimate process.
                  </p>
                </details>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Form Section */}
      <section id="quote-form" className="py-16 bg-gradient-to-br from-slate-900 to-orange-900">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Get Your Free Storm Damage Inspection
              </h2>
              <p className="text-xl text-orange-100">
                Don't wait - storm damage gets worse over time. Connect with qualified contractors today!
              </p>
            </div>
            <QuoteForm />
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Related Roofing Services
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Link to="/services/roof-inspection" className="group bg-gradient-to-br from-orange-50 to-slate-50 rounded-xl p-6 hover:shadow-xl transition-all transform hover:-translate-y-1">
              <i className="ri-search-eye-line text-4xl text-orange-600 mb-4"></i>
              <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-orange-600 transition-colors">
                Free Roof Inspections
              </h3>
              <p className="text-gray-600 mb-4">
                Comprehensive roof inspections to identify all storm damage and potential issues.
              </p>
              <span className="text-orange-600 font-semibold group-hover:underline">
                Learn More <i className="ri-arrow-right-line"></i>
              </span>
            </Link>

            <Link to="/services/insurance-adjuster" className="group bg-gradient-to-br from-orange-50 to-slate-50 rounded-xl p-6 hover:shadow-xl transition-all transform hover:-translate-y-1">
              <i className="ri-file-shield-2-line text-4xl text-orange-600 mb-4"></i>
              <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-orange-600 transition-colors">
                Insurance Claims Help
              </h3>
              <p className="text-gray-600 mb-4">
                Expert assistance navigating insurance claims for storm damage repairs.
              </p>
              <span className="text-orange-600 font-semibold group-hover:underline">
                Learn More <i className="ri-arrow-right-line"></i>
              </span>
            </Link>

            <Link to="/services/shingle-roofs" className="group bg-gradient-to-br from-orange-50 to-slate-50 rounded-xl p-6 hover:shadow-xl transition-all transform hover:-translate-y-1">
              <i className="ri-home-gear-line text-4xl text-orange-600 mb-4"></i>
              <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-orange-600 transition-colors">
                Roof Replacement
              </h3>
              <p className="text-gray-600 mb-4">
                Complete roof replacement services when storm damage is too severe to repair.
              </p>
              <span className="text-orange-600 font-semibold group-hover:underline">
                Learn More <i className="ri-arrow-right-line"></i>
              </span>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default StormDamagePage;
